import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { MailrailAutoSendEmailCampaign } from '@feathr/blackbox';
import { Form } from '@feathr/components';
import type { TValidateGrouped } from '@feathr/rachis';

import type { IActionErrors } from '../../../CampaignSummary';
import MailrailAutomationSchedule from '../../MailrailAutomationSchedule/MailrailAutomationSchedule';
import MailrailPredicatesConfig from '../../MailrailPredicatesConfig/MailrailPredicatesConfig';
import TriggerTypeRadios from '../../TriggerTypeRadios/TriggerTypeRadios';
import { useMailrailTriggerType } from '../useMailrailTriggerType';

interface IProps {
  campaign: MailrailAutoSendEmailCampaign;
  disabled: boolean;
}

interface IErrors extends TValidateGrouped {
  delay_seconds?: string[];
  predicates?: IActionErrors[];
}

export function validateStepAutomation(campaign: MailrailAutoSendEmailCampaign): IErrors {
  return campaign.validate<IErrors>(['delay_seconds', 'predicates'], false, 'grouped').errors;
}

function MailrailCampaignStepAutomation({ campaign, disabled }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const subtype = campaign.get('subtype');
  const isTimeSubtype = campaign.get('subtype') === 'time';

  const { triggerTypeOptions, handleSubtypeChange } = useMailrailTriggerType(campaign);

  const validationErrors = validateStepAutomation(campaign);

  return (
    <Form
      description={t(
        'When should the email in this campaign be sent to people? Configure as many triggers as you like. Activity triggers (like URL visits) can only be combined with other activities. Attribute triggers (like job title) can only be combined with other attributes.',
      )}
      label={t('Edit Campaign: Targeting')}
      title={t('Automation')}
      width={'wide'}
    >
      <TriggerTypeRadios
        disabled={disabled}
        handleSubtypeChange={handleSubtypeChange}
        subtype={subtype}
        triggerTypeOptions={triggerTypeOptions}
      />

      <MailrailPredicatesConfig campaign={campaign} disabled={disabled} />

      {!isTimeSubtype && (
        <MailrailAutomationSchedule
          campaign={campaign}
          disabled={disabled}
          validationErrors={validationErrors}
        />
      )}
    </Form>
  );
}

export default observer(MailrailCampaignStepAutomation);
